'use strict';

var ACTION_TYPES = require('./actionTypes');

/**
 * Action creators for adding, removing, and modifying a product's variants.
 * @type {Object}
 */
var actionCreators = {
  /**
   * Action for when a material is added.
   *
   * @param materialId
   * @param pconf
   * @param existingPconfData
   * @returns {{type: string, payload: {materialIds: string, responseInfo: string|null}}}
   */
  modelMaterialSelected: function modelMaterialSelected(materialIds, responseInfo) {
    return {
      type: ACTION_TYPES.MODEL_MATERIAL_SELECTED,
      payload: {
        materialIds:      materialIds,
        responseInfo:     responseInfo
      }
    };
  },

  /**
   * Action from when a material is removed from the list chooser.
   *
   * @param materialId
   * @returns {{type: string, payload: {materialId: string}}}
   */
  modelMaterialDeselected: function modelMaterialDeselected(materialId) {
    return {
      type: ACTION_TYPES.MODEL_MATERIAL_DESELECTED,
      payload: {
        materialId: materialId
      }
    };
  },

  /**
   * Action to get material info from the server.
   *
   * @param materialId
   * @returns {{type: string, payload: {materialId: string}}}
   */
  getMaterialSelectedInfo: function getMaterialSelectedInfo(materialId) {
    return {
      type: ACTION_TYPES.GET_MODEL_MATERIAL_INFO,
      payload: {
        materialId: materialId
      }
    };
  },

  /**
   * Action for when a model is changed.
   *
   * @param validationInfo object
   * @returns {{type: string, payload: {validationInfo: object}}}
   */
  getModelSelectedInfo: function getModelSelectedInfo(validationInfo) {
    return {
      type: ACTION_TYPES.GET_MODEL_INFO,
      payload: {
        validationInfo: validationInfo
      }
    };
  },

  /**
   * Action for when a model has been selected.
   *
   * @param modelData
   * @param pconfData
   * @returns {{type: string, payload: {responseInfo: object}}}
   */
  modelSelected: function modelSelected(responseInfo) {
    return {
      type: ACTION_TYPES.MODEL_SELECTED,
      payload: {
        responseInfo: responseInfo
      }
    };
  },

  /**
   * Action for when a variant is chosen.
   *
   * @param chosenVariant
   * @returns {{type: string, payload: {chosenVariant: string}}}
   */
  variantSelected: function variantSelected(chosenVariant) {
    return {
      type: ACTION_TYPES.VARIANT_SELECTED,
      payload: {
        chosenVariant: chosenVariant
      }
    };
  },

  /**
   * Action for when a variant is deselected.
   *
   * @returns {{type: string}}
   */
  variantDeselected: function variantDeselected() {
    return {
      type: ACTION_TYPES.VARIANT_DESELECTED
    };
  },

  /**
   *
   * @param optionId string
   * @returns {{type: string, payload: {variantOption: string}}}
   */
  getVariantOptionInfo: function getVariantOptionInfo(optionId) {
    return {
      type: ACTION_TYPES.GET_VARIANT_OPTION_INFO,
      payload: {
        variantOption: optionId
      }
    };
  },

  /**
   * Action for when a variant option is selected.
   *
   * @param variantOption string
   * @param pconf object
   * @param existingPconfData object
   * @returns {{type: string, payload: {variantOption: string, pconfData: object, existingPconfData: object}}}
   */
  variantOptionSelected: function variantOptionSelected(variantOption, responseInfo) {
    return {
      type: ACTION_TYPES.VARIANT_OPTION_SELECTED,
      payload: {
        variantOption:  variantOption,
        responseInfo:   responseInfo
      }
    };
  },


  /**
   * Action for when a variant option is deselected.
   *
   * @param variantOptionId string
   * @returns {{type: string, payload: {variantOption: string}}}
   */
  variantOptionDeselected: function variantOptionDeselected(variantOptionId) {
    return {
      type: ACTION_TYPES.VARIANT_OPTION_DESELECTED,
      payload: {
        variantOptionId: variantOptionId
      }
    };
  },

  /**
   * Action for when a pconf is made active or inactive.
   * @param pconfId string
   * @param isActive bool
   * @returns {{type: string, payload: {pconfId: string|null, forSale: bool}}}
   */
  pconfActivationChanged: function pconfActivationChanged(pconfId, forSale) {
    return {
      type: ACTION_TYPES.PCONF_ACTIVATION_CHANGED,
      payload: {
        pconfId:  pconfId,
        forSale: forSale
      }
    };
  },

  /**
   * Action for when a pconf default is changed.
   *
   * defaultData is an object keyed by its input name (defaultVariantRadio or defaultMaterialRadio) 
   * with a value of its associated material and variant option ID.
   * 
   * @param defaultData obj
   * @returns {{type: string, payload: {defaultData: object}}}
   */
  pconfDefaultChanged: function pconfDefaultChanged(defaultData) {
    return {
      type: ACTION_TYPES.PCONF_DEFAULT_CHANGED,
      payload: {
        defaultData:  defaultData  
      }
    };
  },

  /**
   * Action for when a price on a pconf is changed.
   *
   * @param markupPrice string
   * @param totalPrice string
   * @param pconfId string
   * @returns {{type: string, payload: {markupPrice: string, totalPrice: string, pconfId: string}}}
   */
  pconfPricingChanged: function updatePconfPricing(markupPrice, royalty, totalPrice, pconfId) {
    return {
      type: ACTION_TYPES.PCONF_PRICING_CHANGED,
      payload: {
        markupPrice:  markupPrice,
        totalPrice:   totalPrice,
        royaltyPrice: royalty,
        pconfId:      pconfId
      }
    };
  },

  /**
   * Triggered when the content license or status of the content license changes.
   *
   * @param contentLicenseId int
   * @param contentLicenseStatus int
   * @returns {{type: string, payload: {contentLicenseId: int, contentLicenseStatus: int}}}
   */
  contentLicenseChanged: function contentLicenseChanged(licenseId, isAccepted) {
    return {
      type: ACTION_TYPES.CONTENT_LICENSE_CHANGED,
      payload: {
        licenseId: licenseId,
        isAccepted: isAccepted
      }
    };
  },

  /**
   * To be triggered when a license tag match has been entered.
   * @param licenseId int
   * @returns {{type: string, payload: {licenseId: int}}}
   */
  contentLicenseTagEntered: function contentLicenseTagEntered(licenseId) {
    return {
      type: ACTION_TYPES.CONTENT_LICENSE_TAG_ENTERED,
      payload: {
        licenseId: licenseId
      }
    };
  },


  /**
   * Resets the acceptance to let a user choose whether they opt in or out of a license again.
   * @param resetStatus
   * @returns {{type: string, payload: {resetAcceptance: int}}}
   */
  contentLicenseResetAcceptance: function contentLicenseResetAcceptance(resetStatus) {
    return {
      type: ACTION_TYPES.CONTENT_LICENSE_RESET_ACCEPTANCE,
      payload: {
        resetAcceptance: resetStatus
      }
    };
  },

  /**
   * Updates the grouping option when it changes in the UI.
   * @param grouping string
   * @returns {{type: object, payload: {groupBy: string}}}
   */
  groupByChanged: function groupByChanged(grouping) {
    return {
      type: ACTION_TYPES.GROUP_BY_CHANGED,
      payload: {
        groupBy:  grouping
      }
    };
  },

  /**
   * Action router for a select.
   *
   * Used by ListChooserView to create a generic choosing list
   *
   * @param  actionType   actionType      [description]
   * @param  value        selectedOption  [description]
   * @return object                       [description]
   */
  listChooserSelectAction: function listChooserSelectAction(actionType, selectedOption) {
    switch (actionType) {
      case ACTION_TYPES.GET_MODEL_MATERIAL_INFO:
        return this.getMaterialSelectedInfo(selectedOption);
      case ACTION_TYPES.GET_VARIANT_OPTION_INFO:
        return this.getVariantOptionInfo(selectedOption);
    }

    return {};
  },

  /**
   * Action router for a deselect.
   *
   * Used by ListChooserView to create a generic choosing list
   *
   * @param  actionType   actionType      [description]
   * @param  value        selectedOption  [description]
   * @return object                       [description]
   */
  listChooserDeselectAction: function listChooserDeselectAction(actionType, selectedOption) {
    switch (actionType) {
      case ACTION_TYPES.MODEL_MATERIAL_DESELECTED:
        return this.modelMaterialDeselected(selectedOption);
      case ACTION_TYPES.VARIANT_OPTION_DESELECTED:
        return this.variantOptionDeselected(selectedOption);
    }

    return {};
  }
};

module.exports = actionCreators;
